import { WaveBlockKind } from "./types.js";
const IMAGE_EXT = /\.(jpe?g|png|gif|webp|heic|heif|svg)$/i;
/** Synthetic feed row id from {@link coalesceConsecutiveImageFeedRows} — not a Yjs block id. */
export const COALESCED_GALLERY_BLOCK_ID_PREFIX = "wave-img-group-";
export function sourceBlockIdsFromCoalescedFeedBlockId(blockId) {
    if (!blockId.startsWith(COALESCED_GALLERY_BLOCK_ID_PREFIX))
        return null;
    const suffix = blockId.slice(COALESCED_GALLERY_BLOCK_ID_PREFIX.length);
    if (!suffix.length)
        return null;
    return suffix.split(",");
}
/**
 * Maps a coalesced feed block id to a real GALLERY block id for Yjs mutations.
 * Returns null when `blockId` is not coalesced or no gallery source exists.
 */
export function resolveCoalescedGalleryTargetBlockId(blocks, blockId) {
    const sourceIds = sourceBlockIdsFromCoalescedFeedBlockId(blockId);
    if (!sourceIds?.length)
        return null;
    for (let i = sourceIds.length - 1; i >= 0; i--) {
        const id = sourceIds[i];
        const block = blocks.find((b) => b.id === id);
        if (block?.kind === WaveBlockKind.Gallery)
            return id;
    }
    return null;
}
/** Block that contributes images to a merged gallery row (GALLERY or image FILE). */
export function isWaveImageSourceBlock(block) {
    if (block.kind === WaveBlockKind.Gallery)
        return true;
    if (block.kind !== WaveBlockKind.File)
        return false;
    const mime = (block.payload.mimeType ?? "").toLowerCase();
    const name = (block.payload.fileName ?? "").toLowerCase();
    return mime.startsWith("image/") || IMAGE_EXT.test(name);
}
function galleryItemFromFileBlock(block) {
    if (block.kind !== WaveBlockKind.File)
        return null;
    return {
        id: `file-${block.id}`,
        s3Key: block.payload.s3Key,
        mimeType: block.payload.mimeType,
        fileName: block.payload.fileName,
        fileSize: block.payload.fileSize,
        uploadStatus: block.payload.uploadStatus ?? "ready",
        createdAt: block.createdAt,
    };
}
/** One GALLERY block representing several consecutive image sources. */
export function syntheticGalleryBlockFromSources(sources) {
    const primary = sources.find((b) => b.kind === WaveBlockKind.Gallery) ?? sources[0];
    const items = [];
    for (const b of sources) {
        if (b.kind === WaveBlockKind.Gallery) {
            for (const item of b.payload.galleryItems ?? []) {
                items.push(item);
            }
        }
        else {
            const fromFile = galleryItemFromFileBlock(b);
            if (fromFile)
                items.push(fromFile);
        }
    }
    const ids = sources.map((b) => b.id).join(",");
    return {
        id: `wave-img-group-${ids}`,
        kind: WaveBlockKind.Gallery,
        payload: {
            title: primary.payload.title ?? "Galleria",
            text: primary.payload.text,
            galleryItems: items,
            authorSub: primary.payload.authorSub,
            authorName: primary.payload.authorName,
        },
        sortIndex: primary.sortIndex,
        createdAt: primary.createdAt,
        updatedAt: sources[sources.length - 1]?.updatedAt ?? primary.updatedAt,
    };
}
/** Merge consecutive top-level image FILE/GALLERY rows into a single feed row. */
export function coalesceConsecutiveImageFeedRows(rows) {
    const out = [];
    let buffer = [];
    const flush = () => {
        if (buffer.length === 0)
            return;
        if (buffer.length === 1) {
            out.push(buffer[0]);
        }
        else {
            const sources = buffer.map((r) => r.block);
            const first = buffer[0];
            out.push({
                block: syntheticGalleryBlockFromSources(sources),
                depth: first.depth,
                relation: first.relation,
                coalescedSourceBlockIds: sources.map((b) => b.id),
            });
        }
        buffer = [];
    };
    for (const row of rows) {
        if (row.depth === 0 && isWaveImageSourceBlock(row.block)) {
            buffer.push(row);
        }
        else {
            flush();
            out.push(row);
        }
    }
    flush();
    return out;
}
